import { useRef } from 'react';
import { formatTime, Replay } from '../model/Replay';
import { Icon } from './Icons';

export function Scrubber({
  replay,
  scrubT,
  onChange,
  speed = 1,
  setSpeed,
  playing = false,
  setPlaying,
  showSpeedButtons = true,
}: {
  replay: Replay;
  scrubT: number;
  onChange: (t: number) => void;
  speed?: number;
  setSpeed?: (s: number) => void;
  playing?: boolean;
  setPlaying?: (p: boolean) => void;
  showSpeedButtons?: boolean;
}) {
  const { duration, checkpoints, clusters } = replay.data;
  const tSec = scrubT * duration;
  const tStr = formatTime(tSec * 1000);
  const totalStr = formatTime(duration * 1000);

  const trackRef = useRef<HTMLDivElement>(null);

  function onTrackInteract(e: { clientX: number }) {
    const el = trackRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    onChange(Math.max(0, Math.min(1, x)));
  }

  function onDown(e: React.MouseEvent) {
    onTrackInteract(e);
    const onMove = (ev: MouseEvent) => onTrackInteract(ev);
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-8)',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}
    >
      <button
        onClick={() => setPlaying && setPlaying(!playing)}
        style={{
          width: 36,
          height: 36,
          borderRadius: 'var(--r-6)',
          border: '1px solid var(--border-strong)',
          background: playing ? 'var(--accent-12)' : 'var(--bg-surface-2)',
          color: playing ? 'var(--accent)' : 'var(--fg-1)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {playing ? <Icon.Pause /> : <Icon.Play />}
      </button>

      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
          fontSize: 15,
          fontWeight: 600,
          color: 'var(--fg-1)',
          letterSpacing: '-0.01em',
          minWidth: 92,
        }}
      >
        {tStr}
        <span style={{ color: 'var(--fg-muted)', fontWeight: 400 }}> / {totalStr}</span>
      </div>

      <div
        ref={trackRef}
        onMouseDown={onDown}
        style={{ flex: 1, position: 'relative', height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 12,
            height: 4,
            background: 'var(--bg-surface-3)',
            borderRadius: 2,
          }}
        />
        {clusters.map((c, i) => {
          const x1 = (c.tStart / duration) * 100;
          const x2 = (c.tEnd / duration) * 100;
          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: `${x1}%`,
                width: `${Math.max(0.5, x2 - x1)}%`,
                top: 12,
                height: 4,
                background: 'var(--bad)',
                opacity: 0.7,
                borderRadius: 1,
              }}
            />
          );
        })}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 12,
            height: 4,
            width: `${scrubT * 100}%`,
            background: 'var(--accent)',
            borderRadius: 2,
          }}
        />
        {(checkpoints || []).map((cp, i) => {
          const x = (cp.t / duration) * 100;
          return (
            <div
              key={'cp-' + i}
              style={{
                position: 'absolute',
                left: `${x}%`,
                top: 6,
                transform: 'translateX(-50%)',
                width: 2,
                height: 16,
                background: 'var(--fg-2)',
                opacity: 0.6,
                borderRadius: 1,
              }}
            />
          );
        })}
        <div
          style={{
            position: 'absolute',
            left: `${scrubT * 100}%`,
            top: 0,
            bottom: 0,
            transform: 'translateX(-50%)',
            width: 2,
            background: '#fff',
            boxShadow: '0 0 8px rgba(255,255,255,0.4)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: `${scrubT * 100}%`,
            top: 8,
            transform: 'translateX(-50%)',
            width: 12,
            height: 12,
            background: '#fff',
            borderRadius: '50%',
            boxShadow: '0 0 0 2px var(--accent), 0 0 12px rgba(0,153,255,0.5)',
          }}
        />
      </div>

      {showSpeedButtons && (
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg-surface-2)', padding: 3, borderRadius: 'var(--r-6)' }}>
          {[0.25, 0.5, 1, 2].map((s) => (
            <button
              key={s}
              onClick={() => setSpeed && setSpeed(s)}
              style={{
                padding: '4px 8px',
                minWidth: 36,
                background: speed === s ? 'var(--accent)' : 'transparent',
                color: speed === s ? 'var(--fg-on-accent)' : 'var(--fg-2)',
                border: 'none',
                borderRadius: 4,
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {s}×
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
